import { C, H2, H3, Lead, Note, OL, P, Pre, Table, UL } from '../Doc';

export function Setup() {
  return (
    <>
      <Lead>
        Putting a FormForge form into your own application. Three lines of HTML is the whole
        of the simple case; the rest of this page is what you reach for when the simple case
        is not enough — sizing, theming, locales, restricting who may embed, pre-filling
        answers and reacting to what the respondent does.
      </Lead>

      <Note title="Before you start">
        You need a form that is <strong>published</strong>, and its id. Create one under
        Forms, add questions, then press Publish. The id looks like <C>f_06G83V8E5J…</C> and
        is in the address bar while you are editing it, and on the Share &amp; embed screen.
        A draft has no live version behind it and will not render.
      </Note>

      <H2 id="quick">The quickest embed</H2>
      <P>
        Drop a container where the form should appear and load the script once, anywhere on
        the page. The script finds every container and mounts a form into it.
      </P>
      <Pre title="HTML">{`<div data-formforge="f_06G83V8E5J4BAFKVANYDQGXB3W"></div>
<script src="https://your-formforge-host/embed.js" async></script>`}</Pre>
      <P>
        That is it. The form loads in a sandboxed iframe, sizes itself to its content, and
        submits directly to FormForge — your page never handles the answers, which keeps it
        out of scope for whatever the form collects.
      </P>

      <H3>Getting the snippet from the studio</H3>
      <OL>
        <li>Open the form and go to <strong>Share &amp; embed</strong>.</li>
        <li>Create an embed, choosing how it should present itself.</li>
        <li>Copy the snippet. It carries the form id and the embed id already filled in.</li>
      </OL>
      <P>
        The embed id is optional for rendering but is what carries your settings — allowed
        origins, whether a signed token is required, and the render mode. Without it you get
        the form's defaults.
      </P>

      <H2 id="modes">How the form presents itself</H2>
      <P>
        Set <C>data-mode</C> on the container. Anything other than <C>inline</C> mounts an
        overlay and defaults to opening on click, so put the attribute on a button.
      </P>
      <Table
        head={['Mode', 'Behaves as']}
        rows={[
          [<C key="a">inline</C>, 'Sits in the page flow and grows with its content. The default.'],
          [<C key="b">modal</C>, 'Centred dialog over a dimmed backdrop'],
          [<C key="c">slideover</C>, 'Accepted, but currently presented as a modal'],
          [<C key="d">sidetab</C>, 'Accepted, but currently presented as a modal'],
          [<C key="e">fullpage</C>, 'Takes the whole viewport'],
          [<C key="f">popover</C>, 'Accepted, but currently presented as a modal'],
        ]}
      />
      <Pre title="Open a form from a button">{`<button data-formforge="f_06G8…" data-embed="emb_01H…" data-mode="modal" data-trigger="click">
  Give feedback
</button>
<script src="https://your-formforge-host/embed.js" async></script>`}</Pre>
      <P>
        Overlays trap Escape, close on a click outside, and return focus to whatever opened
        them.
      </P>
      <Note tone="warn" title="Three modes are not distinct yet">
        The loader draws <C>inline</C> and a centred overlay. <C>slideover</C>,{' '}
        <C>sidetab</C> and <C>popover</C> are accepted and stored, but presently render as
        that same overlay. Set them if you want the intent recorded; do not design a layout
        around a side tab until the loader draws one.
      </Note>

      <H2 id="attributes">Every attribute</H2>
      <Table
        head={['Attribute', 'Values', 'What it does']}
        rows={[
          [<C key="a">data-formforge</C>, 'Form id', 'Required. Which form to render.'],
          [<C key="b">data-embed</C>, 'Embed id', 'Applies the embed’s settings: origins, signed-token requirement, render mode.'],
          [<C key="c">data-mode</C>, 'inline · modal · slideover · sidetab · fullpage · popover', 'Defaults to inline.'],
          [<C key="d">data-trigger</C>, 'load · click · delay:2000', 'When to mount. Defaults to load for inline, click for everything else.'],
          [<C key="e">data-height</C>, 'auto · any CSS height', 'auto follows the form’s own height, capped at three viewports.'],
          [<C key="f">data-locale</C>, 'en, fr-CA, …', 'Forces a language the form offers, instead of the browser’s preference.'],
          [<C key="g">data-theme</C>, 'Theme id', 'Renders with a specific theme.'],
          [<C key="h">data-token</C>, 'Signed token', 'Identity and trusted prefill. See below.'],
          [<C key="i">data-on-complete</C>, 'Global function name', 'Called with the completion payload. Prefer the DOM event.'],
        ]}
      />

      <H2 id="events">Reacting to what happens</H2>
      <P>
        The loader turns every message from the form into a DOM event on your container, so
        you listen with ordinary <C>addEventListener</C> and the payload is in{' '}
        <C>event.detail</C>. Events bubble, so one listener on an ancestor can serve several
        forms.
      </P>
      <Pre title="JavaScript">{`const host = document.querySelector('[data-formforge]');

host.addEventListener('formforge:ready', (e) => {
  console.log('form loaded', e.detail.formId);
});

host.addEventListener('formforge:page-change', (e) => {
  console.log(\`page \${e.detail.pageIndex + 1} of \${e.detail.pageCount}\`);
});

host.addEventListener('formforge:complete', (e) => {
  // The response is already saved by the time this fires.
  analytics.track('form_completed', { responseId: e.detail.responseId });
});`}</Pre>
      <Table
        head={['Event', 'Fires when', 'detail carries']}
        rows={[
          [<C key="a">formforge:ready</C>, 'The form has loaded and drawn', 'formId, versionKey'],
          [<C key="b">formforge:resize</C>, 'Its height changed', 'height'],
          [<C key="c">formforge:change</C>, 'An answer changed', 'questionRef, hasValue'],
          [<C key="d">formforge:page-change</C>, 'The respondent moved page', 'pageIndex, pageCount, pageRef, progress'],
          [<C key="e">formforge:validation-error</C>, 'Submit was blocked by validation', 'questionRefs'],
          [<C key="f">formforge:submit</C>, 'Submission started', '—'],
          [<C key="g">formforge:complete</C>, 'The response was accepted', 'responseId'],
          [<C key="h">formforge:error</C>, 'Loading or submitting failed', 'code, retryable'],
          [<C key="i">formforge:session-started</C>, 'A resumable session opened', 'resumeToken'],
        ]}
      />
      <Note>
        Values in <C>change</C> events are deliberately not included — only whether the
        question now has a value. The answers go to FormForge, not through your page.
      </Note>

      <H3>Opening a form from your own code</H3>
      <Pre title="JavaScript">{`// Any container the loader has already seen
window.FormForge.open('f_06G8…');

// After rendering containers yourself, in a single-page app
window.FormForge.scan();`}</Pre>
      <P>
        You rarely need <C>scan()</C>: the loader watches the document and picks up
        containers added later, which is what makes it work in a React or Vue app without
        extra wiring.
      </P>

      <H2 id="origins">Restricting where it may be embedded</H2>
      <P>
        An embed can name the origins allowed to host it. Requests from anywhere else are
        refused, so a copied snippet does not work on someone else's site. Add origins on the
        Share &amp; embed screen, or through the API:
      </P>
      <Pre>{`POST /v1/embeds/{embedId}/origins
     { "origin": "https://app.example.com" }`}</Pre>
      <Note tone="warn">
        List every origin you actually serve from, including staging and any preview
        domains. An origin is scheme, host and port — <C>https://example.com</C> does not
        cover <C>https://www.example.com</C>.
      </Note>

      <H2 id="tokens">Pre-filling, and knowing who is answering</H2>
      <P>
        Two ways to get a value into a form, and they are trusted differently:
      </P>
      <Table
        head={['Source', 'Trusted', 'Use for']}
        rows={[
          ['Query parameter', 'No — anyone can edit a URL', 'Non-sensitive conveniences, such as a campaign name'],
          ['Signed token', 'Yes — minted by your server', 'Who the respondent is, and anything that must not be forged'],
        ]}
      />
      <P>
        Mint a token from your backend and pass it as <C>data-token</C>. Because your server
        holds the API key, the browser never sees a credential.
      </P>
      <Pre title="Your server">{`POST /v1/embeds/{embedId}/token
     Authorization: Bearer ffk_live_…
     { "subject": "user_1234", "claims": { "plan": "pro" } }
  -> { "token": "…" }`}</Pre>
      <Pre title="Your page">{`<div data-formforge="f_06G8…" data-embed="emb_01H…" data-token="…"></div>`}</Pre>
      <Note tone="warn" title="Prefill is opt-in per question">
        A question only accepts a pre-filled value if its author ticked <em>prefillable</em>.
        An embed cannot silently pre-answer whatever it likes — if a value is not landing,
        check that box first.
      </Note>
      <P>
        An embed can also be set to <strong>require</strong> a signed token, which stops it
        rendering at all without one. That is the setting to use when a form should only ever
        be answered by a signed-in user of your product.
      </P>

      <H2 id="alternatives">If you cannot use the script</H2>
      <H3>A plain iframe</H3>
      <P>
        The loader is a convenience over an iframe you can write yourself. You lose automatic
        resizing and the DOM events unless you handle <C>postMessage</C> directly.
      </P>
      <Pre title="HTML">{`<iframe
  src="https://your-formforge-host/e?f=f_06G8…&e=emb_01H…&o=https://app.example.com"
  style="border:0;width:100%;height:640px"
  title="Feedback form"
  allow="camera; microphone; geolocation; clipboard-write"></iframe>`}</Pre>
      <Table
        head={['Parameter', 'Meaning']}
        rows={[
          [<C key="a">f</C>, 'Form id — required'],
          [<C key="b">e</C>, 'Embed id'],
          [<C key="c">o</C>, 'Your origin, so the form knows where to post messages'],
          [<C key="d">m</C>, 'Render mode'],
          [<C key="e">l</C>, 'Locale'],
          [<C key="f">t</C>, 'Theme id'],
          [<C key="g">k</C>, 'Signed token'],
        ]}
      />

      <H3>A hosted link</H3>
      <P>
        No embedding at all: share the link from the Share &amp; embed screen, or send
        single-use links so each recipient can answer exactly once. Good for email, and for
        anywhere you cannot run script.
      </P>

      <H3>The API directly</H3>
      <P>
        Build your own interface and post answers yourself. That is the{' '}
        <strong>Developer documentation</strong> page.
      </P>

      <H2 id="checklist">Before you go live</H2>
      <UL>
        <li>The form is <strong>published</strong>, not a draft.</li>
        <li>Every origin you serve from is on the embed's allowlist, staging included.</li>
        <li>If answers must be attributable, the embed requires a signed token and your server mints it.</li>
        <li>Tokens are minted server-side. An API key in front-end code is a leaked API key.</li>
        <li>You have handled <C>formforge:error</C> — networks fail and forms get closed.</li>
        <li>You have tried it on a phone: overlays and auto-height behave differently in a small viewport.</li>
        <li>Your Content-Security-Policy allows framing the FormForge host if you set <C>frame-src</C>.</li>
      </UL>

      <H2 id="troubleshooting">When it does not work</H2>
      <Table
        head={['Symptom', 'Usual cause']}
        rows={[
          ['Nothing renders', 'The form is a draft, or the id is wrong. A draft has no published version to serve.'],
          ['Renders locally, blank in production', 'The production origin is not on the embed’s allowlist.'],
          ['Refuses to load with a token error', 'The embed requires a signed token and none was passed, or it has expired.'],
          ['Height stays tiny or is clipped', <>Something sets a fixed height on the iframe, or <C>data-height</C> is not <C>auto</C>.</>],
          ['Prefill is ignored', 'The question is not marked prefillable.'],
          ['Events never fire', <>The listener is on the wrong element — they are dispatched on the container carrying <C>data-formforge</C>, though they bubble.</>],
          ['Two forms on one page interfere', 'Give each container its own embed id, and listen on each container rather than the document.'],
        ]}
      />

      <H2 id="self-host">Running FormForge itself</H2>
      <P>
        This page is about consuming FormForge from another application. If you are instead
        setting up the platform — database, migrations, row-level security, configuration —
        that lives in the repository: <C>README.md</C> for the run steps and{' '}
        <C>docs/CONFIGURATION.md</C> for every setting.
      </P>
    </>
  );
}
